import { useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import Image from 'next/image';
import { oneLine } from 'common-tags';
import { Form, Formik } from 'formik';
import { number, object } from 'yup';

import { Trash } from '@/icons';
import { CustomSkeletonTheme, NextLink, Price, Text } from '@/atoms';
import { IconButton, NumberInput } from '@/molecules';

import { priceToNumber, toCurrency } from '@/lib/utils';
import styles from './CartLine.module.css';

import type { FormikProps, FormikValues } from 'formik';
import type { FunctionComponent } from 'react';
import type { TypeCartLineProps } from './types';

const QuantitySchema = object().shape({
  quantity: number().required(),
});

/**
 * CartLine
 */
export const CartLine: FunctionComponent<TypeCartLineProps> = ({
  className = '',
  link,
  size = 'normal',
  image,
  name,
  description,
  quantity = 1,
  price,
  regularPrice = '',
  disabled = false,
  onChange,
  onRemove,
  loading,
  editable = true,
}: TypeCartLineProps) => {
  // TODO: Añadir tipos
  const initialValues: any = { quantity: quantity };
  const formikRef = useRef<FormikProps<FormikValues>>(null);

  useEffect(() => {
    if (editable && !loading && formikRef.current) {
      formikRef.current.setFieldValue('quantity', quantity);
    }
  }, [quantity]);

  return (
    <CustomSkeletonTheme>
      <div
        className={oneLine`
          ${styles['c-cart-line']}
          ${size !== 'normal' ? styles['c-cart-line--large'] : ''}
          ${className}
        `}
        data-playwright="cartLine"
      >
        <div className={styles['c-cart-line__content']}>
          {loading ? (
            <Skeleton className={styles['c-cart-line__image']} />
          ) : (
            link &&
            image && (
              <NextLink href={link}>
                <a className={styles['c-cart-line__image']}>
                  <Image
                    className="w-full"
                    layout="fill"
                    src={image.src}
                    alt={image.alt ?? ''}
                    draggable="false"
                    objectPosition="center"
                    objectFit="contain"
                  />
                </a>
              </NextLink>
            )
          )}

          <div className="flex flex-grow flex-col justify-between gap-y-4 pl-4 md:py-3 md:pl-6">
            <div className="flex flex-row items-start justify-between gap-3">
              <div className="w-full">
                {loading ? (
                  <Skeleton
                    className={styles['c-cart-line__name']}
                    containerClassName="block w-full"
                  />
                ) : (
                  link && (
                    <NextLink href={link}>
                      <a
                        className={styles['c-cart-line__name']}
                        data-playwright="cartLine-name"
                      >
                        {name}
                      </a>
                    </NextLink>
                  )
                )}

                {description && !loading && (
                  <p className="u-font-card u-font-card--subtitle text-typo-secondary">
                    {description}
                  </p>
                )}

                {quantity && price && quantity > 1 && !loading ? (
                  <p className="u-font-card u-font-card--subtitle text-typo-secondary">
                    Unidad: {toCurrency(priceToNumber(price) / quantity)}
                  </p>
                ) : null}
              </div>

              {onRemove && !loading && editable && (
                <IconButton
                  size="small"
                  style="outline"
                  disabled={disabled || loading}
                  onClick={onRemove}
                  data-playwright="cartLine-trash"
                >
                  <Trash />
                </IconButton>
              )}
            </div>

            <div className="flex flex-row items-end justify-between gap-x-5">
              {loading ? (
                <Skeleton className="h-8" containerClassName="w-20 block" />
              ) : editable && onChange ? (
                <Formik
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  onSubmit={() => {}}
                  initialValues={initialValues}
                  validationSchema={QuantitySchema}
                  innerRef={formikRef}
                  enableReinitialize
                >
                  <Form>
                    <NumberInput
                      name="quantity"
                      placeholder="quantity"
                      id="quantity"
                      disabled={disabled || loading}
                      onChange={onChange}
                    />
                  </Form>
                </Formik>
              ) : (
                <Text className="u-body u-body--xs font-bold">
                  {quantity} uds.
                </Text>
              )}

              <Price
                {...(loading
                  ? { loading: true }
                  : { price: price ?? '', regularPrice: regularPrice })}
              />
            </div>
          </div>
        </div>
      </div>
    </CustomSkeletonTheme>
  );
};
